import { Component, OnInit } from "@angular/core";

import { MatMenuModule } from "@angular/material/menu";
import { ActivatedRoute, Params } from "@angular/router";
import { AccountMarketplace, Marketplace, ProductGroup } from "@front/m19-api-client";
import { Catalog, ProductGroupEx } from "@front/m19-models";
import { AccountSelectionService, AsinService, ProductGroupService } from "@front/m19-services";
import { IButtonComponent, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";
import { EditableTextComponent } from "@m19-board/shared/editable-text/editable-text.component";
import { ICON_ADD, ICON_CHEVRON_DOWN, ICON_CLOSE, ICON_EDIT_O } from "@m19-board/utils/iconsLabels";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { LoadingService } from "@m19-board/services/loading.service";
import { NavBreadcrumbDirective } from "@m19-board/containers/page-layout/nav-breadcrumb.directive";

@UntilDestroy()
@Component({
  selector: "app-product-group-page",
  templateUrl: "./product-group-page.component.html",
  styleUrls: ["./product-group-page.component.scss"],
  standalone: true,
  imports: [
    TranslocoDirective,
    MatMenuModule,
    IButtonComponent,
    AsinSelectionComponent,
    EditableTextComponent,
    NavBreadcrumbDirective,
  ],
})
export class ProductGroupPageComponent implements OnInit {
  productGroup?: ProductGroupEx | null;
  alreadySelectedProducts?: string[];
  newAsins: string[] = [];
  marketplace?: Marketplace;
  accountId?: string;
  isReadOnly = false;
  editMode = false;

  readonly ICON_EDIT = ICON_EDIT_O;
  readonly ICON_CHEVRON_DOWN = ICON_CHEVRON_DOWN;

  private catalog?: string[];

  constructor(
    private route: ActivatedRoute,
    private productGroupService: ProductGroupService,
    private asinService: AsinService,
    private accountSelectionService: AccountSelectionService,
    private toastrService: ToasterService,
    private translocoService: TranslocoService,
    private loadingService: LoadingService,
  ) {}

  ngOnInit() {
    this.loadingService.loadingOn();
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(untilDestroyed(this))
      .subscribe((am: AccountMarketplace) => {
        this.marketplace = am.marketplace;
        this.accountId = am.accountId;
      });
    combineLatest<[Params, ProductGroupEx[]]>([
      this.route.params,
      this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
        switchMap((am) => this.productGroupService.getProductGroups(am.accountId, am.marketplace)),
      ),
    ])
      .pipe(
        map(([params, productGroups]) => {
          const id = Number(params["id"]);
          return productGroups.find((pg) => pg.productGroupId == id);
        }),
      )
      .subscribe((productGroup: ProductGroupEx | undefined) => {
        this.loadingService.loadingOff();
        this.productGroup = productGroup;
        this.alreadySelectedProducts = productGroup?.items;
      });

    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        switchMap((am: AccountMarketplace) => this.asinService.getCatalog(am.accountId, am.marketplace)),
      )
      .subscribe((catalog: Catalog) => {
        this.catalog = [...catalog.childAsins.values()];
      });
    this.accountSelectionService.readOnlyMode$.pipe(untilDestroyed(this)).subscribe((b) => (this.isReadOnly = b));
  }

  changeName(newname: string) {
    this.productGroupService
      .updateProductGroupName(
        this.accountId!,
        this.marketplace!,
        (this.productGroup as ProductGroup).productGroupId!,
        newname,
      )
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.toastrService.success(this.translocoService.translate("product-group.product_group_name_updated"));
        },
        error: (err) => {
          this.toastrService.error(
            err,
            this.translocoService.translate("product-group-page.error_updating_product_group_name"),
          );
        },
      });
  }

  updateProductGroupProducts(newAsins: string[]) {
    const toAdd = newAsins.filter((a) => !this.alreadySelectedProducts?.includes(a));
    const toRemove = this.alreadySelectedProducts?.filter((a) => !newAsins.includes(a));
    if (toAdd && toAdd.length > 0) {
      this.addAsinsArrayToProductGroup(toAdd);
    }
    if (toRemove && toRemove.length > 0) {
      this.deleteAsinArrayFromProductGroup(toRemove);
    }
  }

  addAsinsArrayToProductGroup(asins: string[]) {
    this.productGroupService
      .addAsinsToProductGroup(
        this.accountId!,
        this.marketplace!,
        (this.productGroup as ProductGroupEx).productGroupId,
        asins,
        this.catalog!,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("product-group-page.asins_added_to_product_group"),
          );
          this.newAsins = [];
          this.editMode = false;
        },
        error: (err) => {
          this.toastrService.error(
            err,
            this.translocoService.translate("product-group-page.error_adding_asins_to_product_group"),
          );
        },
      });
  }

  deleteAsinArrayFromProductGroup(asins: string[]) {
    if (this.isReadOnly) {
      return;
    }
    this.productGroupService
      .deleteAsinsFromProductGroup(
        this.accountId!,
        this.marketplace!,
        (this.productGroup as ProductGroupEx).productGroupId,
        asins,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("product-group-page.asins_removed_from_product_group"),
          );
          this.newAsins = [];
          this.editMode = false;
        },
        error: (err) => {
          this.toastrService.error(
            err,
            this.translocoService.translate("product-group-page.error_removing_asins_from_product_group"),
          );
        },
      });
  }

  protected readonly ICON_ADD = ICON_ADD;
  protected readonly ICON_EDIT_O = ICON_EDIT_O;
  protected readonly ICON_CLOSE = ICON_CLOSE;
}
